import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */

export const _frontmatter = {
  "pageTitle": "Popover API",
  "title": "Popover",
  "props": ["Popover", "PopoverProps", "PopoverPosition", "PopoverApi", "PopoverTrigger", "PopoverTriggerProps", "PopoverContent", "PopoverContentProps", "PopoverHeader", "PopoverFooter", "PopoverSectionProps"]
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};
const PageContent = makeShortcode("PageContent");
const LeadParagraph = makeShortcode("LeadParagraph");
const Link = makeShortcode("Link");
const PopoverProps = makeShortcode("PopoverProps");
const ButtonProps = makeShortcode("ButtonProps");
const IconButtonProps = makeShortcode("IconButtonProps");
const PopoverContentProps = makeShortcode("PopoverContentProps");
const PopoverSectionProps = makeShortcode("PopoverSectionProps");
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <PageContent componentName="popover" type="api" mdxType="PageContent">
      <LeadParagraph mdxType="LeadParagraph">
  A <b>Popover</b> is a small UI element that appears on top of content when
  triggered, showing additional information or actions.
      </LeadParagraph>
      <h2>{`Basic Usage`}</h2>
      <p>{`The `}<inlineCode parentName="p">{`Popover`}</inlineCode>{` component is composed of two main parts: the trigger element (provided by the `}<inlineCode parentName="p">{`PopoverTrigger`}</inlineCode>{` component) and the content container (provided by the `}<inlineCode parentName="p">{`PopoverContent`}</inlineCode>{` component). When the trigger is activated (via click or hover), the popover content is displayed.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverHeader,
  PopoverFooter,
} from 'react-magma-dom';

export function Example() {
  return (
    <Popover>
      <PopoverTrigger>Click me</PopoverTrigger>
      <PopoverContent>
        <PopoverHeader>
          <div>Popover Title</div>
        </PopoverHeader>
        <div>This is the main content of the popover</div>
        <PopoverFooter>
          <div>Footer Content</div>
        </PopoverFooter>
      </PopoverContent>
    </Popover>
  );
}
`}</code></pre>
      <h2>{`Hoverable Popover`}</h2>
      <p>{`By setting the `}<inlineCode parentName="p">{`hoverable`}</inlineCode>{` prop to true, the popover will remain open while the pointer is over either the trigger or the content.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import { Popover, PopoverTrigger, PopoverContent } from 'react-magma-dom';

export function Example() {
  return (
    <Popover hoverable>
      <PopoverTrigger aria-label="Open popover">Hover over me</PopoverTrigger>
      <PopoverContent>
        <div>This popover stays open on hover</div>
      </PopoverContent>
    </Popover>
  );
}
`}</code></pre>
      <h2>{`Popover with Pointer`}</h2>
      <p>{`The `}<inlineCode parentName="p">{`hasPointer`}</inlineCode>{` prop controls the display of an arrow pointer that visually connects the popover to its trigger element. By default, `}<inlineCode parentName="p">{`hasPointer`}</inlineCode>{` is `}<inlineCode parentName="p">{`true`}</inlineCode>{`. Set `}<inlineCode parentName="p">{`hasPointer={false}`}</inlineCode>{` to disable the pointer.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import { Popover, PopoverTrigger, PopoverContent } from 'react-magma-dom';

export function Example() {
  return (
    <Popover hasPointer={false}>
      <PopoverTrigger aria-label="Open popover">
        Click for popover without pointer
      </PopoverTrigger>
      <PopoverContent>
        <div>Content without an arrow pointer</div>
      </PopoverContent>
    </Popover>
  );
}
`}</code></pre>
      <h2>{`Controlled Popover with API Ref`}</h2>
      <p>{`You can programmatically control the popover by providing an `}<inlineCode parentName="p">{`apiRef`}</inlineCode>{`. The Popover API exposes methods such as `}<inlineCode parentName="p">{`closePopoverManually`}</inlineCode>{` to close the popover when needed.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverApi,
  Button,
} from 'react-magma-dom';

export function Example() {
  const popoverApiRef = React.useRef<PopoverApi>();

  function handleClose(event: React.SyntheticEvent) {
    if (popoverApiRef.current) {
      popoverApiRef.current.closePopoverManually(event);
    }
  }

  return (
    <Popover apiRef={popoverApiRef}>
      <PopoverTrigger aria-label="Toggle popover">
        Toggle Popover
      </PopoverTrigger>
      <PopoverContent>
        <div>
          Popover Content
          <Button onClick={handleClose}>Close Popover</Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
`}</code></pre>
      <h2>{`Focus Trap`}</h2>
      <p>{`When the `}<inlineCode parentName="p">{`focusTrap`}</inlineCode>{` prop is enabled, keyboard focus is trapped within the popover while it is open. This is especially useful when the popover contains interactive elements.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverFooter,
  FormGroup,
  Toggle,
  Checkbox,
  Button,
  ButtonSize,
  PopoverApi,
  CheckboxTextPosition,
} from 'react-magma-dom';

export function Example() {
  const popoverApiRef = React.useRef<PopoverApi>();

  function handleClose(event: React.SyntheticEvent) {
    if (popoverApiRef.current) {
      popoverApiRef.current.closePopoverManually(event);
    }
  }

  return (
    <Popover focusTrap width="target">
      <PopoverTrigger aria-label="Open popover">
        Open Focus Trap Popover
      </PopoverTrigger>
      <PopoverContent>
        <FormGroup>
          <Toggle
            labelText="Setting 1"
            defaultChecked
            containerStyle={{
              width: '100%',
              justifyContent: 'space-between',
            }}
          />
          <Checkbox
            labelText="Setting 2"
            textPosition={CheckboxTextPosition.left}
            labelStyle={{ width: '100%', justifyContent: 'space-between' }}
          />
          <Checkbox
            labelText="Setting 3"
            textPosition={CheckboxTextPosition.left}
            labelStyle={{ width: '100%', justifyContent: 'space-between' }}
          />
        </FormGroup>
        <PopoverFooter style={{ justifyContent: 'end', fontWeight: 600 }}>
          <Button size={ButtonSize.small} onClick={handleClose}>
            Apply
          </Button>
        </PopoverFooter>
      </PopoverContent>
    </Popover>
  );
}
`}</code></pre>
      <h2>{`Custom Width and Max Height`}</h2>
      <p>{`Customize the size of your popover using the `}<inlineCode parentName="p">{`width`}</inlineCode>{` and `}<inlineCode parentName="p">{`maxHeight`}</inlineCode>{` props. You can provide these values as strings (with units) or as numbers (interpreted as pixels). Using `}<inlineCode parentName="p">{`width=“target”`}</inlineCode>{` will set the width of the content based on the width of the trigger.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import { Popover, PopoverTrigger, PopoverContent } from 'react-magma-dom';

export function Example() {
  return (
    <Popover width="300px" maxHeight="200px">
      <PopoverTrigger aria-label="Open popover">
        Custom Size Popover
      </PopoverTrigger>
      <PopoverContent>
        <div>This popover has a fixed width and maximum height</div>
      </PopoverContent>
    </Popover>
  );
}
`}</code></pre>
      <h2>{`Popover Positions`}</h2>
      <p>{`The `}<inlineCode parentName="p">{`position`}</inlineCode>{` prop sets the default position for the popover in relation to its trigger. It uses `}<Link to='https://floating-ui.com/' mdxType="Link">{`floating-ui`}</Link>{` to move the position if there is not enough space on the screen. The available options are `}<inlineCode parentName="p">{`PopoverPosition.bottom`}</inlineCode>{` (default) and `}<inlineCode parentName="p">{`PopoverPosition.top`}</inlineCode>{`.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverPosition,
} from 'react-magma-dom';

export function Example() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <Popover position={PopoverPosition.bottom}>
        <PopoverTrigger aria-label="Open bottom popover">
          Bottom Popover
        </PopoverTrigger>
        <PopoverContent>
          <div>This popover appears below the trigger</div>
        </PopoverContent>
      </Popover>
      <Popover position={PopoverPosition.top}>
        <PopoverTrigger aria-label="Open top popover">
          Top Popover
        </PopoverTrigger>
        <PopoverContent>
          <div>This popover appears above the trigger</div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
`}</code></pre>
      <h2>{`Popover Sections`}</h2>
      <p>{`To structure the content of your popover, use the `}<inlineCode parentName="p">{`PopoverHeader`}</inlineCode>{` and `}<inlineCode parentName="p">{`PopoverFooter`}</inlineCode>{` components to add dedicated header and footer sections.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverHeader,
  PopoverFooter,
} from 'react-magma-dom';

export function Example() {
  return (
    <Popover>
      <PopoverTrigger aria-label="Open popover">
        Sectioned Popover
      </PopoverTrigger>
      <PopoverContent>
        <PopoverHeader>
          <div>Header Section</div>
        </PopoverHeader>
        <div>Main content of the popover goes here</div>
        <PopoverFooter>
          <div>Footer Section</div>
        </PopoverFooter>
      </PopoverContent>
    </Popover>
  );
}
`}</code></pre>
      <h2>{`Popover Trigger Variants`}</h2>
      <p>{`The `}<inlineCode parentName="p">{`PopoverTrigger`}</inlineCode>{` component supports both icon-only and text-based triggers. For icon-only triggers, be sure to provide an appropriate `}<inlineCode parentName="p">{`aria-label`}</inlineCode>{` for accessibility.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  ButtonSize,
  FormGroup,
  Checkbox,
} from 'react-magma-dom';
import { FilterAltIcon } from 'react-magma-icons';

export function Example() {
  return (
    <Popover width={160}>
      <PopoverTrigger
        icon={<FilterAltIcon />}
        size={ButtonSize.large}
        aria-label="Open popover"
      />
      <PopoverContent>
        <FormGroup>
          <Checkbox labelText="Filter 1" />
          <Checkbox labelText="Filter 2" />
          <Checkbox labelText="Filter 3" />
          <Checkbox labelText="Filter 4" />
        </FormGroup>
      </PopoverContent>
    </Popover>
  );
}
`}</code></pre>
      <h2>{`Disabled Popover / Disabled Trigger`}</h2>
      <p>{`The `}<inlineCode parentName="p">{`isDisabled`}</inlineCode>{` property will disable the ability to open the popover. For disabled triggers with enabled content, you need to place the disabled button inside the `}<inlineCode parentName="p">{`PopoverTrigger`}</inlineCode>{` and use the `}<inlineCode parentName="p">{`hoverable`}</inlineCode>{` property.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  Button,
} from 'react-magma-dom';

export function Example() {
  return (
    <div style={{ display: 'flex', gap: 8 }}>
      <Popover isDisabled>
        <PopoverTrigger aria-label="Open popover">
          Disabled Popover
        </PopoverTrigger>
        <PopoverContent>
          <div>Popover is disabled</div>
        </PopoverContent>
      </Popover>
      <Popover hoverable>
        <PopoverTrigger aria-label="Open popover">
          <Button disabled>Disabled Trigger</Button>
        </PopoverTrigger>
        <PopoverContent>
          <div>Popover is enabled, trigger is disabled</div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
`}</code></pre>
      <h2>{`Open by Default`}</h2>
      <p>{`The `}<inlineCode parentName="p">{`openByDefault`}</inlineCode>{` property will allow Popover to open as soon as the page loads. It is recommended to use localStorage to modify this behavior.`}</p>
      <p><strong parentName="p">{`Note:`}</strong>{` For a proper demonstration, open the example, uncheck the “Don’t show this again” checkbox, and then refresh the page.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import {
  Popover,
  PopoverApi,
  PopoverTrigger,
  PopoverContent,
  Button,
  FormGroup,
  Checkbox,
  ButtonSize,
} from 'react-magma-dom';

export function Example() {
  const popoverApiRef = React.useRef<PopoverApi>();

  function handleClose(event: React.SyntheticEvent) {
    if (popoverApiRef.current) {
      popoverApiRef.current.closePopoverManually(event);
    }
  }

  const sessionStorageValue = JSON.parse(
    sessionStorage.getItem('temporaryOpenByDefault')!
  );

  const initialOpen = React.useMemo(() => {
    return sessionStorageValue || false;
  }, []);

  const [checked, setChecked] = React.useState(() => initialOpen);

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextValue = !event.target.checked;

    sessionStorage.setItem('temporaryOpenByDefault', JSON.stringify(nextValue));

    setChecked(nextValue);
  };

  return (
    <Popover openByDefault={initialOpen} apiRef={popoverApiRef} width={320}>
      <PopoverTrigger aria-label="Popover trigger" />
      <PopoverContent>
        <span>
          Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do
          eiusmod tempor incididunt.
        </span>
        <FormGroup
          containerStyle={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Checkbox
            labelText="Don’t show this again"
            onChange={onChange}
            checked={!checked}
          />
          <Button size={ButtonSize.small} onClick={handleClose}>
            Got it
          </Button>
        </FormGroup>
      </PopoverContent>
    </Popover>
  );
}
`}</code></pre>
      <h2>{`Inverse`}</h2>
      <p>{`The `}<inlineCode parentName="p">{`isInverse`}</inlineCode>{` property is an optional boolean, that reverses the colors so that the component can better appear on a dark background. The default value is `}<inlineCode parentName="p">{`false`}</inlineCode>{`.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import {
  Card,
  CardBody,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverHeader,
  PopoverFooter,
} from 'react-magma-dom';

export function Example() {
  return (
    <Card isInverse>
      <CardBody>
        <Popover isInverse>
          <PopoverTrigger>Click me</PopoverTrigger>
          <PopoverContent>
            <PopoverHeader>
              <div>Popover Title</div>
            </PopoverHeader>
            <div>This is the main content of the popover</div>
            <PopoverFooter>
              <div>Footer Content</div>
            </PopoverFooter>
          </PopoverContent>
        </Popover>
      </CardBody>
    </Card>
  );
}
`}</code></pre>
      <h2>{`Popover Props`}</h2>
      <p><strong parentName="p">{`Any additional props supplied will be provided to the wrapping div element.`}</strong></p>
      <PopoverProps mdxType="PopoverProps" />
      <h2>{`Popover Trigger Props`}</h2>
      <p><strong parentName="p">{`PopoverTrigger accept the same props as the `}<Link to="/api/button" mdxType="Link">{`Button`}</Link>{` component.`}</strong></p>
      <ButtonProps mdxType="ButtonProps" />
      <p>{`The PopoverTrigger can also accept an `}<inlineCode parentName="p">{`icon`}</inlineCode>{` property, as in the `}<Link to="/api/icon-button" mdxType="Link">{`Icon Button`}</Link>{` component.`}</p>
      <IconButtonProps mdxType="IconButtonProps" />
      <h2>{`Popover Content Props`}</h2>
      <p><strong parentName="p">{`Props supplied will be passed to the wrapping div element containing the popover content.`}</strong></p>
      <PopoverContentProps mdxType="PopoverContentProps" />
      <h2>{`Popover Section Props`}</h2>
      <p><strong parentName="p">{`Any additional props supplied will be provided to the wrapping section element.`}</strong></p>
      <PopoverSectionProps mdxType="PopoverSectionProps" />
    </PageContent>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      